<script setup>
import { ref, computed } from 'vue'
import { useForm } from '@inertiajs/vue3'

const props = defineProps({
  client_nom:           { type: String, default: 'auRapport' },
  client_logo:          { type: String, default: null },
  client_logo_is_url:   { type: Boolean, default: false },
  alert:                { type: String, default: null },
  notice:               { type: String, default: null },
  submit_url:           { type: String, default: '/utilisateurs/password' },
  sign_in_url:          { type: String, default: '/utilisateurs/sign_in' },
  param_key:            { type: String, default: 'utilisateur' },
  reset_password_token: { type: String, default: '' },
  errors:               { type: Object, default: () => ({}) },
})

const logoSrc = computed(() => {
  if (!props.client_logo) return null
  return props.client_logo_is_url ? props.client_logo : `/images/client/${props.client_logo}`
})

const showPassword = ref(false)
const showConfirm  = ref(false)

const form = useForm({
  reset_password_token:  props.reset_password_token,
  password:              '',
  password_confirmation: '',
})

function submit() {
  form.transform(data => ({ [props.param_key]: data })).put(props.submit_url)
}

const currentYear = new Date().getFullYear()
</script>

<template>
  <div class="min-h-screen bg-surface text-on-surface font-body flex flex-col">

    <!-- Split layout -->
    <div class="flex flex-1">

      <!-- Panneau gauche — branding -->
      <div class="hidden lg:flex lg:w-[55%] relative flex-col items-center justify-center px-16 bg-surface overflow-hidden">

        <div class="absolute top-0 right-0 w-[500px] h-[500px] bg-primary-container rounded-full blur-[140px] opacity-10 -translate-y-1/3 translate-x-1/3 pointer-events-none"></div>
        <div class="absolute bottom-0 left-0 w-[350px] h-[350px] bg-tertiary-container rounded-full blur-[120px] opacity-10 translate-y-1/3 -translate-x-1/3 pointer-events-none"></div>

        <div class="relative z-10 flex flex-col items-start gap-8 max-w-md w-full">
          <div class="flex items-center gap-4">
            <img
              v-if="client_logo"
              :src="logoSrc"
              alt="Logo"
              class="h-12 w-auto object-contain"
            />
            <span v-else class="material-symbols-outlined text-primary text-5xl">shield</span>
          </div>

          <h1 class="text-5xl font-black tracking-widest uppercase text-primary leading-tight">
            {{ client_nom }}
          </h1>

          <p class="text-on-surface-variant text-lg leading-relaxed max-w-sm">
            Choisissez un nouveau mot de passe sécurisé.
          </p>

          <div class="w-16 h-1 rounded-full machined-sheen"></div>
        </div>
      </div>

      <!-- Panneau droit — formulaire -->
      <div class="w-full lg:w-[45%] flex flex-col items-center justify-center px-8 py-12 bg-surface-container-low relative">

        <div class="lg:hidden flex items-center gap-3 mb-10 self-start">
          <span class="material-symbols-outlined text-primary text-3xl">shield</span>
          <h1 class="text-primary font-black tracking-widest uppercase text-lg">{{ client_nom }}</h1>
        </div>

        <!-- Card formulaire -->
        <div class="w-full max-w-md bg-surface-container rounded-xl shadow-2xl overflow-hidden">

          <div class="px-8 pt-8 pb-4 text-center">
            <h2 class="text-2xl font-bold tracking-tight text-on-surface mb-2">
              Nouveau mot de passe
            </h2>
            <p class="text-on-surface-variant text-sm">
              Entrez puis confirmez votre nouveau mot de passe.
            </p>
          </div>

          <!-- Notice succès -->
          <div v-if="notice" class="mx-8 mt-2 px-4 py-3 rounded-lg bg-tertiary/10 text-tertiary text-sm flex items-center gap-2 border border-tertiary/20">
            <span class="material-symbols-outlined text-base">check_circle</span>
            <span>{{ notice }}</span>
          </div>

          <!-- Alerte -->
          <div v-if="alert || form.errors?.reset_password_token || form.errors?.password || form.errors?.password_confirmation"
               class="mx-8 mt-2 px-4 py-3 rounded-lg bg-error-container text-on-error-container text-sm flex items-center gap-2">
            <span class="material-symbols-outlined text-base">error</span>
            <span>{{ alert || form.errors?.reset_password_token || form.errors?.password || form.errors?.password_confirmation }}</span>
          </div>

          <!-- Formulaire -->
          <form class="px-8 pt-6 pb-8 space-y-6" @submit.prevent="submit">

            <input type="hidden" v-model="form.reset_password_token" />

            <!-- Nouveau mot de passe -->
            <div class="space-y-2">
              <label class="block text-xs font-bold uppercase tracking-wider text-outline" for="password">
                Nouveau mot de passe
              </label>
              <div class="relative group">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span class="material-symbols-outlined text-outline group-focus-within:text-primary transition-colors text-xl">lock</span>
                </div>
                <input
                  id="password"
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="••••••••"
                  autocomplete="new-password"
                  autofocus
                  required
                  class="block w-full pl-10 pr-10 py-3 bg-surface-container-highest border-none rounded-lg text-on-surface placeholder-outline focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-outline hover:text-on-surface transition-colors"
                  @click="showPassword = !showPassword"
                >
                  <span class="material-symbols-outlined text-xl">
                    {{ showPassword ? 'visibility_off' : 'visibility' }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Confirmation -->
            <div class="space-y-2">
              <label class="block text-xs font-bold uppercase tracking-wider text-outline" for="password_confirmation">
                Confirmer le mot de passe
              </label>
              <div class="relative group">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span class="material-symbols-outlined text-outline group-focus-within:text-primary transition-colors text-xl">lock</span>
                </div>
                <input
                  id="password_confirmation"
                  v-model="form.password_confirmation"
                  :type="showConfirm ? 'text' : 'password'"
                  placeholder="••••••••"
                  autocomplete="new-password"
                  required
                  class="block w-full pl-10 pr-10 py-3 bg-surface-container-highest border-none rounded-lg text-on-surface placeholder-outline focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-outline hover:text-on-surface transition-colors"
                  @click="showConfirm = !showConfirm"
                >
                  <span class="material-symbols-outlined text-xl">
                    {{ showConfirm ? 'visibility_off' : 'visibility' }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Bouton submit -->
            <div class="pt-2">
              <button
                type="submit"
                :disabled="form.processing"
                class="machined-sheen w-full py-4 px-6 rounded-lg text-on-primary font-black tracking-widest uppercase flex items-center justify-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span v-if="form.processing" class="material-symbols-outlined text-xl animate-spin">progress_activity</span>
                <span v-else>Réinitialiser le mot de passe</span>
                <span v-if="!form.processing" class="material-symbols-outlined text-xl">check</span>
              </button>
            </div>

            <!-- Retour connexion -->
            <div class="text-center pt-2">
              <a :href="sign_in_url" class="inline-flex items-center gap-1.5 text-xs text-outline hover:text-primary font-bold transition-all">
                <span class="material-symbols-outlined text-sm">arrow_back</span>
                Retour à la connexion
              </a>
            </div>
          </form>

          <div class="bg-surface-container-low px-8 py-5 text-center">
            <p class="text-on-surface-variant text-xs">
              Problèmes de connexion ? Contactez le support technique.
            </p>
          </div>
        </div>
      </div>
    </div>

    <footer class="py-6 text-center text-outline text-[11px] tracking-widest uppercase bg-surface">
      Copyright &copy; 2016-{{ currentYear }} aurapport.com inc.
    </footer>
  </div>
</template>
